import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RevenueIcon } from '../../../assets/icons/sidebar/revenue-sharing-icon.svg';
import { brandText } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { AccountRevenueSharingInterFace } from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchAccountRevenueSharingList,
  fetchListAccountRevenueSharingFilters,
  updateAccountRevenueSharingFilters,
  setSortData,
  refreshAccountRevenueSharingList,
} from '../../../redux/slices/AccountRevenueSharing';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { disablePaymentManagementApplied } from '../../../redux/slices/PaymentManagementSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import revenueSharingApi from '../../../utils/api/revenue';

/**
 * Revenue sharing component
 * @returns {React.JSX.Element} returning jsx element
 */
function AccountRevenueSharing() {
  const { t, i18n } = useTranslation('revenue');

  const dispatch = useAppDispatch();
  const {
    revenueList,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.revenueSharing);

  const [modalView, setModalView] = useState({
    status: false,
    title: t('addAccount'),
    header: true,
    content: <div />,
    rightCorner: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Fetch Users list function
   * @param {any} data - The request data
   */
  const fetchRevenueSharing = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading) {
      dispatch(fetchAccountRevenueSharingList(data));
    }
  };
  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchRevenueSharing({ ...value, ...queryParameters, sortField, sortBy });
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Rendering converted UTC date and time
       * @param RowData - current row data
       * @returns - returns span element with date and time
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ConvertUTCtoDateTime(RowData.createdAt)}</span>
      ),
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: `${t('totalPowerConsumed')} (Kwh)`,
      accessorKey: 'totalPowerConsumedInKwh' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Rendering total power consumption
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.powerConsumedInKwh, 3)} kwh</span>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: 'transactionId' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: `${t('totalAmount')} ${brandText.currency}`,
      accessorKey: 'totalAmount' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * total power
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.totalAmount)}</span>
      ),
    },
    {
      label: t('partnerRevenueByPercentage'),
      accessorKey: 'partnerRevenuePercentage' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering partner revenue by percentage
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.partnerRevenuePercentage)}</span>
      ),
    },
    {
      label: t('partnerRevenueByCost'),
      accessorKey: 'partnerRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering partner revenue cost
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.partnerRevenue)}</span>
      ),
    },
    {
      label: t('cpoRevenueByPercentage'),
      accessorKey: 'cpoRevenuePercentage' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering CPO revenue by percentage
       * @param {AccountRevenueSharingInterFace} RowData - The row data
       * @returns {React.JSX.Element} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.cpoRevenuePercentage)}</span>
      ),
    },
    {
      label: t('cpoRevenueByCost'),
      accessorKey: 'cpoRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering CPO revenue by cost
       * @param {AccountRevenueSharingInterFace} RowData - The row data
       * @returns {React.JSX.Element} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.cpoRevenue)}</span>
      ),
    },
  ];

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('accountName'), accessorKey: 'accountName' },
    { label: t('accountId'), accessorKey: 'accountCustomId' },
    { label: t('stationId'), accessorKey: 'chargingStationId' },
    { label: `${t('totalPowerConsumed')} (Kwh)`, accessorKey: 'totalMeterDifference' },
    { label: t('transactionId'), accessorKey: 'transactionId' },
    { label: `${t('totalAmount')} (${brandText.currency})`, accessorKey: 'totalAmount' },
    { label: t('partnerRevenueByPercentage'), accessorKey: 'partnerRevenuePercentage' },
    { label: t('partnerRevenueByCost'), accessorKey: 'partnerRevenue' },
    { label: t('cpoRevenuePercentage'), accessorKey: 'cpoRevenuePercentage' },
    { label: t('cpoRevenueByCost'), accessorKey: 'cpoRevenue' },
  ];

  /**
   * Handle auto refresh function
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshAccountRevenueSharingList({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortField && sortBy) {
      payload.sortField = sortField;
      payload.sortBy = sortBy;
    }
    revenueSharingApi
      .fetchRevenueSharingExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AccountRevenueSharingInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row.createdAt ? ConvertUTCtoDateTime(row.createdAt) : t('na');

              case 'accountCustomId':
                return row.accountCustomId || t('na');

              case 'totalMeterDifference':
                return row.powerConsumedInKwh
                  ? ToFixedValue(Number(row.powerConsumedInKwh))
                  : '0.00';

              case 'totalAmount':
                return row.totalAmount ? ToFixedValue(row.totalAmount) : '0.00';

              case 'transactionId':
                return row.transactionId ? row.transactionId : '0';

              case 'partnerRevenuePercentage':
                return row.partnerRevenuePercentage
                  ? ToFixedValue(row.partnerRevenuePercentage)
                  : '0.00';

              case 'partnerRevenue':
                return row.partnerRevenue ? ToFixedValue(row.partnerRevenue) : '0.00';

              case 'cpoRevenuePercentage':
                return row.cpoRevenuePercentage ? ToFixedValue(row.cpoRevenuePercentage) : '0.00';

              case 'cpoRevenue':
                return row.cpoRevenue ? ToFixedValue(row.cpoRevenue) : '0.00';

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-sharing-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-sharing-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchRevenueSharing({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchRevenueSharing({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || revenueList.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchRevenueSharing({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePaymentManagementApplied());
    }
  }, [filters]);

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('revenueSharing'), icon: <RevenueIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <CustomTable
        header={column}
        version="v2"
        height={window.innerHeight >= 700 ? 337 : 320}
        data={revenueList}
        serialNo
        sizeChangeButtonRequired
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        filters={filters}
        filtersToDispatch={updateAccountRevenueSharingFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListAccountRevenueSharingFilters}
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
        refershCurrentPages={handleRefresh}
        exportDataPdfOrCSV={
          permissionCheck('cpoRevenueSharingExport') ? exportDataPdfOrCSV : undefined
        }
        exportLoader={exportLoader}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
      />
    </div>
  );
}

export default AccountRevenueSharing;
